<script setup lang="ts">
  import type { StateMachineContext } from '~/models/StateMachineModels'
  import { VerifyResult } from '~/generated/api-clients-generated'
  import type { HpForm } from '#components'
  import { ProfileStore } from '~/stores/profile'
  import type { PushToSaveScenarios } from '~/composables/PushToSave'

  const props = defineProps<{
    context: StateMachineContext
    presentation: string
  }>()

  const { verify } = Auth.use()
  const { hydratePlaceholders } = Cms.use()

  const scenario = computed(() => (props.context?.config ?? 'add-needs') as PushToSaveScenarios)
  const { data } = await PushToSaveContent.use(scenario.value)

  const screen = computed(() => data.value?.screens?.verify)

  const session = SessionStore.use()
  const code = ref<string | undefined>(undefined)
  const form = ref<InstanceType<typeof HpForm> | null>(null)
  const loading = ref<boolean>(false)

  const { otpValidation } = AccountValidation.use()

  const text = computed(() =>
    hydratePlaceholders(screen.value?.text, {
      identifier: session.authBag.identifier?.replace('@', '<wbr />@')
    })
  )

  const profile = ProfileStore.use()

  const onRetry = () => {
    props.context.goTo('retry')
  }

  const onSubmit = async () => {
    loading.value = true

    const result = await verify(code.value!)

    switch (result) {
      case VerifyResult.Success:
        await SessionStore.load()
        await profile.load()
        props.context.next()
        break
      case VerifyResult.InvalidPasscode:
        form.value?.setErrors({
          'account-code': screen.value?.errors?.invalidPasscode ?? 'An error occurred'
        })
        break
      default:
        form.value?.setErrors({
          'account-code': screen.value?.errors?.unknown ?? 'An error occurred'
        })
        break
    }

    loading.value = false
  }
</script>

<template>
  <div class="mb-4 grid place-content-center place-items-center gap-3 text-center">
    <span class="text-5xl leading-none">{{ screen?.icon }}</span>
    <h2>{{ screen?.heading }}</h2>
    <p
      v-if="text"
      class="text-balance rounded-heading bg-sky-100 p-4 text-center leading-normal"
      v-html="text"
    />
  </div>

  <HpForm
    id="verify-form"
    ref="form"
    :validation-schema="otpValidation"
    :on-submit="onSubmit"
    class="flex w-full flex-col items-start gap-y-4"
  >
    <HpFormInput
      id="account-code"
      class="w-full"
      :label="data?.labels?.code"
      :placeholder="data?.labels?.code"
      v-model="code"
      type="tel"
    />
    <p class="w-full text-balance">
      {{ screen?.newCodeOption?.text }}
      <HpAnchor @click.prevent="onRetry" class="font-semibold">
        {{ screen?.newCodeOption?.cta }}
      </HpAnchor>
    </p>
    <HpButton class="mt-12" :busy="loading">Submit</HpButton>
  </HpForm>
</template>

<style scoped></style>
